import ApiErrorBanner from '@/components/ApiErrorBanner';
import type { AnimalFiltersEntity } from '@/components/Colony/Animals/Animals.utils';
import Loading from '@/components/Loading';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { updateSettings } from '@/components/UI/Table/Reusable/Cache.utils';
import type { TanStackColumns, FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _isNotEmpty, _notNil, uuid } from '@/littledash';
import { Animal, AnimalDefinitionV1 } from '@/model/Animal.model';
import type { State } from '@/model/State.model';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { useFetchContext, useFetchEntity } from '@/support/Hooks/fetch';
import type { UseFetchEntityOutput } from '@/support/Hooks/fetch/useFetchEntity';
import { RouteService } from '@/support/RouteService';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { animalBulkActions, animalFilterOptions, animalTableColumns } from './AnimalsTable.utils';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import ActionList, { ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList.tsx';
import SelectDropDown from '@/components/UI/SelectDropDown';
import Link from '@/components/UI/Link';
import { modalAction } from '@/utils/modal.tsx';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook.ts';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { web as webRoute } from '@/support/route.ts';
import { AnimalCreateV1 } from '@/model/PlannedAnimal.model.ts';
import { ApiService } from '@/support/ApiService.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import InVivoError from '@/model/InVivoError.ts';
import { mapMetadataResponse, MetadataGlossaryItem } from '@/components/Studies/Create/Steps/Animals/Animals.utils.tsx';
import Button from '@/components/UI/Button';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors.tsx';

const searchByOptions = [
  { value: 'name', display: 'Name' },
  { value: 'tail', display: 'Tail' },
  { value: 'ear', display: 'Ear' },
  { value: 'tag', display: 'Tag' },
  { value: 'donor', display: 'Donor' },
  { value: 'animal_number', display: 'Animal No.' },
  { value: 'cage_name', display: 'Cage' },
  { value: 'cage_number', display: 'Cage No.' },
];

interface ContextProps {
  entity: Study;
}

interface AnimalsTableProps {
  enableBulkActions?: boolean;
  onSelectRow?: () => void;
  onFetchTableData?: () => void;
  queryParams?: Record<string, string>;
  tableMaxHeight?: number;
  plannedAnimals?: Array<AnimalDefinitionV1>;
  fetchPlannedAnimals?: () => Promise<void>;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  bulkRecordObservations: features?.bulk_record_observations ?? false,
  plannedAnimalsEnabled: features?.planned_animals ?? false,
  atunesIntegrationEnabled: features?.atunes_colony_management_integration ?? false,
}));

const inReviewSelector = (state: State) => state.ui.settings.studyState.pageStates.animals;

const AnimalsTable: FC<AnimalsTableProps> = ({
  enableBulkActions = true,
  onSelectRow,
  onFetchTableData,
  queryParams,
  tableMaxHeight,
  plannedAnimals,
  fetchPlannedAnimals,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [columns, setColumns] = useState<TanStackColumns<Animal>>([]);
  const [refetchToken, setRefetchToken] = useState<string>(uuid());
  const { entity: study } = useFetchContext<ContextProps>();
  const calculations = study?.settings?.calculations;
  const { study_groups: studyGroups, id: studyId } = study;
  const { openModal } = modalAction(useDispatch());
  const { bulkRecordObservations, plannedAnimalsEnabled, atunesIntegrationEnabled } = useSelector(featureSelector);
  const inReview = useSelector(inReviewSelector);

  const { response: animalsResponse, invoke: fetchAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: study?.api_id },
    query: { perPage: 5 },
    invokeOnInit: true,
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const animalCount = animalsResponse?.body?.data?.length ?? 0;

  const { response: speciesResponse } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const { response: groupsResponse } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: study?.id },
    query: { perPage: -1, include: ['animals'] },
    options: { invokeOnInit: true, onError: { slug: 'fetch-study-groups', throw: false } },
  });

  const { response: metadataResponse } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'subject_meta',
    },
    invokeOnInit: true,
  });

  const { entity: observationOptions }: UseFetchEntityOutput<Array<string>> = useFetchEntity({
    entityType: 'observationFilterOptions',
    params: { id: studyId },
  });

  const { entity: animalFilters, entityError: animalFiltersError }: UseFetchEntityOutput<AnimalFiltersEntity> =
    useFetchEntity({
      entityType: 'animalFilters',
    });

  const settings = useSelector((state: State) => state.ui.settings);

  const buildColumns = async () => {
    if (_notNil(animalFilters)) {
      const columns = animalTableColumns(
        studyId,
        calculations ?? [],
        settings.tables.animals.columns,
        settings.changePercentage,
        animalFilters.metadata,
        bulkRecordObservations
      );
      setColumns(columns);
      setLoading(false);
    }
  };

  useEffect(() => {
    buildColumns();
  }, [animalFilters, calculations, settings, bulkRecordObservations]);

  const updatedSettings = useMemo(() => {
    // Ensure any derived columns (e.g: from measurements) are listed in the settings.
    return updateSettings('animals', columns, settings);
  }, [columns, settings]);

  const dispatch = useDispatch();
  const history = useHistory();

  const hasAccess = Auth.isWriteUserForStudy(study);
  const bulkActions: (props: UseTableProps<Animal>) => Array<unknown> = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { resetTableState },
    },
  }) =>
    hasAccess && enableBulkActions
      ? animalBulkActions(
          selectedFlatRows,
          resetTableState,
          study,
          dispatch,
          history,
          atunesIntegrationEnabled,
          bulkRecordObservations,
          inReview,
          fetchPlannedAnimals
        )
      : [];

  useEffect(() => {
    if (
      _notNil(calculations) &&
      _notNil(studyGroups) &&
      _notNil(studyId) &&
      _notNil(observationOptions) &&
      _notNil(animalFilters)
    ) {
      setFilterOptions(animalFilterOptions(calculations, studyGroups, studyId, observationOptions, animalFilters));
    }
  }, [calculations, studyGroups, studyId, observationOptions, animalFilters]);

  const handleCreateNewAnimals = () => {
    history.push(
      webRoute('colony.add.study', {
        studyId: study.id,
      })
    );
  };

  const handleCreateFromPlannedAnimals = () => {
    const species = speciesResponse?.body?.data ?? [];
    const groups = ((groupsResponse?.body as { data: Array<TreatmentGroup> })?.data ?? []).map((g) => ({
      ...g,
      animals_count: g.animals?.length ?? 0,
    }));
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const metadata = mapMetadataResponse((metadataResponse?.body || []) as Array<MetadataGlossaryItem>);

    openModal('CREATE_ANIMALS', {
      plannedAnimals,
      selectedIds: new Set(plannedAnimals?.map((a) => a.api_id)),
      studyId: study?.id,
      groups,
      species,
      studyMetadata: metadata,
      onSubmit: async (animals: AnimalCreateV1) => {
        try {
          await ApiService.call({
            endpoint: 'POST /api/v1/studies/{studyApiId}/planned/animals/create-animals',
            path: { studyApiId: study?.api_id },
            body: animals,
            options: { onError: { toast: false } },
          });
          successToast('Successfully created animals');
          await fetchAnimals();
          await fetchPlannedAnimals?.();
          setRefetchToken(uuid());
        } catch (err) {
          errorToast('Could not create animals');
          ExceptionHandler.captureException(
            new InVivoError('Could not create animals', {
              cause: err,
              slug: 'create-from-planned-animals',
            })
          );
        }
      },
    });
  };

  let actions: ActionProps[] = [];

  if ((plannedAnimals?.length ?? 0) > 0 && plannedAnimalsEnabled) {
    actions = [
      {
        name: 'Create new animals',
        action: () => handleCreateNewAnimals(),
      },
      {
        name: 'Create from planned animals',
        action: () => handleCreateFromPlannedAnimals(),
      },
    ];
  }

  return (
    <div className="pb4" data-test-component="AnimalsTable" data-test-element="animals-table-container">
      {loading ? (
        <div style={{ height: 650 }}>
          <Loading txt="Loading animals..." />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
          {animalFiltersError && (
            <ApiErrorBanner
              title="There was an error fetching table data"
              text="Please try again later. If this keeps occurring please contact support."
              error={animalFiltersError}
              className="mb4"
            />
          )}
          {animalCount > 0 || !plannedAnimalsEnabled || inReview ? (
            <APITable
              apiInfo={{
                type: 'internalApi',
                route: RouteService.api({
                  endpoint: 'GET /api/v1/studies/{studyId}/animals',
                  path: { studyId: study?.api_id },
                }).url,
              }}
              queryParams={queryParams}
              includeParam={`latestMeasurement,study_group,alerts,cage,metadata${
                bulkRecordObservations === true ? ',latestObservations' : ''
              }`}
              columns={columns}
              bulkActions={bulkActions}
              filterOptions={filterOptions}
              defaultSortBy={{ id: 'number', desc: false }}
              searchPlaceholderText="Search"
              searchQueryByOptions={searchByOptions}
              settings={updatedSettings}
              reduxTableName={'animals'}
              showMetricChangeFrom={true}
              onSelectRow={onSelectRow}
              pageSizes={[50, 100, 200, 500, 1000]}
              defaultPageSize={1000}
              onFetchTableData={onFetchTableData}
              testIdPrefix="animals"
              maxHeight={tableMaxHeight}
              refetch={refetchToken}
              customFilterProcessor={createMultiSelectDoubleEncodingProcessor(animalFilters?.metadata ?? [])}
            />
          ) : (
            <div className={`ui-card mv4 pa4`} style={{ minHeight: 190 }}>
              <div data-testid="create-animals-container" className="w-100" data-test-component="CreateAnimals">
                <div className="br2 bg-near-white tc flex items-center justify-center flex-column pa4">
                  <div className="mw6 center ph4 pa4">
                    <h3 className="mb2">{atunesIntegrationEnabled ? 'Import Animals' : 'Create animals'}</h3>
                    <p className="mb3">
                      {atunesIntegrationEnabled
                        ? 'To import animals and cages from tick@lab go to '
                        : 'To import cages from colony, go to '}
                      <Link className="link" to={`/studies/${study.id}/cages/`}>
                        cages
                      </Link>
                      .
                    </p>
                    {!atunesIntegrationEnabled &&
                      (_isNotEmpty(actions) ? (
                        <SelectDropDown
                          title="Create animals"
                          alignMenu="right"
                          className="pr4"
                          testId="create-animals-select-dropdown"
                        >
                          <ActionList actions={actions} />
                        </SelectDropDown>
                      ) : (
                        <Button onClick={() => handleCreateNewAnimals()} className="pr4" testId="create-animals-button">
                          Create new animals
                        </Button>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnimalsTable;
